import { CloseIcon } from '@chakra-ui/icons';
import {
  Button,
  DrawerHeader,
  Flex,
  Heading,
  IconButton,
  Tag,
  Text,
} from '@chakra-ui/react';
import {
  ExternalIngestionDocumentFragment,
  ExternalIngestionFolderFragment,
  ExternalValidationReportDataFragment,
  IngestionDocumentReviewFragment,
} from '@client/graphql/__generated__/types';
import { FC, ReactNode } from 'react';
import { formatUnderscoreField } from '~/common/utils/formatter';
import { ComplianceValidationVariant } from './ComplianceDrawer';
import { CompliancePlatformLinkButton } from './CompliancePlatformLinkButton';
import { FolderValidationFeedback } from './FolderValidationFeedback';

interface ComplianceDrawerHeaderProps {
  variant: ComplianceValidationVariant;
  currentFolder: ExternalIngestionFolderFragment | null | undefined;
  validationData: ExternalValidationReportDataFragment | null;
  documents: ExternalIngestionDocumentFragment[];
  documentReviews?: IngestionDocumentReviewFragment[];
  submittedDocumentReviews?: IngestionDocumentReviewFragment[];
  onClose: () => void;
  onViewSummary?: () => void;
  additionalActions?: ReactNode;
}

export const ComplianceDrawerHeader: FC<ComplianceDrawerHeaderProps> = ({
  variant,
  currentFolder,
  validationData,
  documents,
  documentReviews,
  submittedDocumentReviews,
  onClose,
  onViewSummary,
  additionalActions,
}) => {
  // Check if any documents need review (either doc.needsReview is true OR document has a submitted review)
  const submittedReviewsByDocId = new Set(
    submittedDocumentReviews?.map((review) => review.ingestionDocument.id) || []
  );
  const hasDocumentsNeedingReview = documents.some(
    (doc) => doc.needsReview || submittedReviewsByDocId.has(doc.id)
  );
  const hasDocumentReviews = documentReviews && documentReviews.length > 0;

  return (
    <DrawerHeader
      bg="compliance.header.bg"
      borderBottomWidth="1px"
      borderColor="compliance.header.border"
    >
      <Flex align="center" justify="space-between">
        <Flex alignItems="center" gap={8}>
          <Flex direction="column">
            <Heading color="compliance.header.text" size="headline">
              {currentFolder?.folderName}
            </Heading>
            {currentFolder && (
              <CompliancePlatformLinkButton
                ingestionFolder={currentFolder}
                variant={variant}
              />
            )}
          </Flex>
          <Flex align="center" gap={2}>
            <Tag size="lg" textTransform="uppercase">
              {formatUnderscoreField(currentFolder?.transactionSide ?? '')}
            </Tag>
            <Tag size="lg" textTransform="uppercase">
              {formatUnderscoreField(currentFolder?.status ?? '')}
            </Tag>
          </Flex>
        </Flex>

        <Flex align="center" gap={2}>
          {/* Additional folder actions */}
          {additionalActions}
          {validationData?.folderValidation && (
            <FolderValidationFeedback
              folderValidation={validationData.folderValidation}
              variant={variant}
            />
          )}
          {variant === 'external' && hasDocumentsNeedingReview && (
              <Button
                bg={hasDocumentReviews ? 'white' : 'whiteAlpha.200'}
                isDisabled={!hasDocumentReviews}
                size="sm"
                variant="solid"
                onClick={onViewSummary}
              >
                <Flex align="center" gap={1}>
                  <Text
                    color={hasDocumentReviews ? 'gray.600' : 'gray.400'}
                    fontWeight="normal"
                  >
                    Summary
                  </Text>
                  <Text
                    color={hasDocumentReviews ? 'black' : 'white'}
                    fontWeight="medium"
                  >
                    {documentReviews?.length ?? 0} Changes
                  </Text>
                </Flex>
              </Button>
            )}
          <IconButton
            aria-label="Close drawer"
            color="compliance.header.text"
            icon={<CloseIcon />}
            size="sm"
            variant="link"
            onClick={onClose}
          />
        </Flex>
      </Flex>
    </DrawerHeader>
  );
};
